import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Clock,
    AlertCircle,
    Gavel,
    MapPin,
    Phone,
    UserCheck,
    FileText,
    CalendarDays,
    AlertTriangle,
    User,
    Briefcase,
    Link,
    KeyRound,
    FileDigit,
    Download,
    XCircle,
    Calendar,
    Check, 
} from 'lucide-react';
import api from '../services/api';
import * as XLSX from 'xlsx';

/**
 * Checks if a single event is past due.
 */
const isEventOverdue = (event, selectedDateStr) => {
    const now = new Date();
    // Use selectedDateStr to ensure the date is correct
    const eventDateTime = new Date(selectedDateStr + 'T' + (event.hearingTime || '23:59'));
    const isPast = eventDateTime < now;
    const statusLower = (event.hearingStatus || '').toLowerCase();
    const isCompleted = statusLower.includes('completed');
    const isCancelled = statusLower.includes('cancelled');
    
    return isPast && !isCompleted && !isCancelled;
};


/**
 * Renders the list of hearings for a single selected day.
 */
const HearingDayList = ({ selectedDate, events, onViewDetails }) => {
    if (!selectedDate || !events || events.length === 0) {
        return null;
    }

    const getStatusBadgeColor = (status) => {
        const s = (status || '').toLowerCase();
        if (s.includes('scheduled')) return 'bg-blue-100 text-blue-700';
        if (s.includes('completed')) return 'bg-green-100 text-green-700';
        if (s.includes('cancelled')) return 'bg-red-100 text-red-700';
        if (s.includes('postponed')) return 'bg-yellow-100 text-yellow-700';
        if (s.includes('waiting for payment')) return 'bg-orange-100 text-orange-800';
        
        if (s.includes('pending') || s === '') {
            return 'bg-orange-100 text-orange-800';
        }
        
        return 'bg-gray-100 text-gray-700';
    };

    return (
        <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Events for {new Date(selectedDate + 'T00:00:00').toLocaleDateString('default', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}
            </h3>
            
            {events.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                    {events.map((event, idx) => {
                        const overdue = isEventOverdue(event, selectedDate);
                        
                        return (
                            <div key={`${event.recordId}-${idx}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                                <div className="flex justify-between items-start gap-4">
                                
                                    {/* Left Side: Event Details */}
                                    <div className="flex-1 min-w-0">
                                        
                                        {/* Row 1: Badges (Status, Time, Overdue) */}
                                        <div className="flex items-center flex-wrap gap-2 mb-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(event.hearingStatus)}`}>
                                                {event.hearingStatus || 'Pending'}
                                            </span>
                                            {event.hearingTime && (
                                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {event.hearingTime}
                                                </span>
                                            )}
                                            {overdue && (
                                                <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" />Overdue
                                                </span>
                                            )}
                                        </div>
                                        
                                         {/* Row 2: Patient & Provider Details */}
                                         <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700 mb-3">
                                                <span className="flex items-start gap-2">
                                                <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <strong className="text-xs text-gray-500">Provider:</strong>
                                                    <div className="font-medium">{event.provider || 'N/A'}</div>
                                                </div>
                                                </span>
                                                <span className="flex items-start gap-2">
                                                <User className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <strong className="text-xs text-gray-500">Patient:</strong>
                                                    <div className="font-medium">{event.ptName}</div>
                                                </div>
                                                </span>
                                   
                                            </div>
                                            <div className="flex items-start gap-2 md:col-span-2">
                                                <FileDigit className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <strong className="text-xs text-gray-500">ADJ NUMBER:</strong>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {(event.adjNumber && event.adjNumber.length > 0) ? (
                                                            event.adjNumber.map((adj, i) => {
                                                                const adjDisplayValue = (typeof adj === 'object' && adj.value) ? adj.value : adj;
                                                                return (
                                                                    <span key={i} className="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full font-mono">
                                                                        {adjDisplayValue}
                                                                    </span>
                                                                );
                                                            })
                                                        ) : <span className="text-xs text-gray-500">N/A</span>}
                                                    </div>
                                                </div>
                                            </div>
                                   {/* Row 3: Hearing & Judge Details */}
                                   <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700 mb-3">
                                             <span className="flex items-start gap-2">
                                                <Gavel className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <strong className="text-xs text-gray-500">Judge:</strong>
                                                    <div className="font-medium">{event.judgeName || 'N/A'}</div>
                                                </div>
                                            </span>
                                            <span className="flex items-start gap-2">
                                                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <strong className="text-xs text-gray-500">Location:</strong>
                                                    <div className="font-medium">{event.boardLocation || 'N/A'}</div>
                                                </div>
                                            </span>
                                            {event.judgePhone && (
                                                <span className="flex items-start gap-2">
                                                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <strong className="text-xs text-gray-500">Judge Contact:</strong>
                                                        <div>
                                                            <a href={`tel:${event.judgePhone}`} className="text-indigo-600 hover:underline font-medium">
                                                                {event.judgePhone}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </span>
                                            )}
                                    </div>

                                    {/* Row 4: Virtual & Case Details */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-700">
                                              {event.courtRoomlink && (
                                                  <span className="flex items-start gap-2">
                                                      <Link className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                      <div>
                                                          <strong className="text-xs text-gray-500">Virtual Link:</strong>
                                                          <div>
                                                              <a href={event.courtRoomlink.startsWith('http') ? event.courtRoomlink : `//${event.courtRoomlink}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium break-all">
                                                                  {event.courtRoomlink}
                                                              </a>
                                                          </div>
                                                      </div>
                                                  </span>
                                              )}
                                              {event.AccesCode && (
                                                  <span className="flex items-start gap-2">
                                                      <KeyRound className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                      <div>
                                                          <strong className="text-xs text-gray-500">Access Code:</strong>
                                                          <div className="font-medium">{event.AccesCode}</div>
                                                      </div>
                                                  </span>
                                              )}
                                             <span className="flex items-start gap-2">
                                                  <UserCheck className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                                  <div>
                                                      <strong className="text-xs text-gray-500">Assigned Collector:</strong>
                                                      <div className="font-medium">{event.assignedCollector?.username || 'Unassigned'}</div>
                                                  </div>
                                              </span>
                                    </div>
                                    
                                    {/* --- NEW: Commented By Section (Matches ScheduleCalendar style) --- */}
                                    {event.author && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            Commented By: {event.author.username || 'Unknown'}
                                        </div>
                                    )}

                                   </div>

                                   {/* Right Side: Button */}
                                   <div className="flex flex-col items-end gap-2 shrink-0">
                                         {onViewDetails && (
                                             <button
                                                 onClick={() => onViewDetails(event.recordId)}
                                                 type="button"
                                                 title="View record details"
                                                 className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-md transition-colors flex items-center gap-1 w-full justify-center"
                                             >
                                                 <FileText className="w-3 h-3" />View Record
                                             </button>
                                         )}
                                   </div>
                                   
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="text-gray-500 text-center py-4">No scheduled events for this date</p>
            )}
        </div>
    );
};


/**
 * Main HearingCalendar Component
 */
const HearingCalendar = ({ onViewDetails }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // State for viewing events below the calendar
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDateEvents, setSelectedDateEvents] = useState([]);

    // --- State for the Download ---
    const [downloadStartDate, setDownloadStartDate] = useState('');
    const [downloadEndDate, setDownloadEndDate] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    
    // --- MODIFIED: State for specific date download ---
    const [downloadMode, setDownloadMode] = useState('range'); // 'range' or 'specific'
    const [specificDownloadDates, setSpecificDownloadDates] = useState(new Set()); // Holds YYYY-MM-DD strings


    const detailsRef = useRef(null);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // Fetch Calendar Events (This is for the monthly view)
    useEffect(() => {
        const fetchHearingEvents = async () => {
            setLoading(true);
            setError(null);
            try {
                const firstDayOfMonth = new Date(year, month, 1);
                const lastDayOfMonth = new Date(year, month + 1, 0);

                const params = {
                    startDate: firstDayOfMonth.toISOString().split('T')[0],
                    endDate: lastDayOfMonth.toISOString().split('T')[0],
                };

                const response = await api.get('/records/hearing-events', { params });

                const eventsByDate = response.data.reduce((acc, event) => {
                    const eventDate = new Date(event.hearingDate);
                    const date = new Date(Date.UTC(eventDate.getUTCFullYear(), eventDate.getUTCMonth(), eventDate.getUTCDate())).toISOString().split('T')[0];
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(event);
                    return acc;
                }, {});

                setEvents(eventsByDate);
            } catch (err) {
                setError('Failed to fetch hearing events.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchHearingEvents();
    }, [year, month]);

    // --- NEW: Sync effect to update list when events load (needed for Today button month switch) ---
    useEffect(() => {
        if (selectedDate && events[selectedDate]) {
            setSelectedDateEvents(events[selectedDate]);
        }
    }, [events, selectedDate]);


    // Calendar Grid Logic
    const calendarGrid = useMemo(() => {
        const firstDayOfWeek = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const prevMonthDays = new Date(year, month, 0).getDate();
        const days = [];
        
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            const day = prevMonthDays - i;
            days.push({ key: `prev-${day}`, day, isCurrentMonth: false, events: [] });
        }
        
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = events[dateStr] || [];
            const hasPastDue = dayEvents.some(event => isEventOverdue(event, dateStr));
            
            days.push({ 
                key: dateStr, 
                day, 
                isCurrentMonth: true, 
                dateStr, 
                events: dayEvents,
                hasPastDue
            });
        }
        
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            days.push({ key: `next-${day}`, day, isCurrentMonth: false, events: [] });
        }
        return days;
    }, [year, month, events]);


    // Calendar Handlers
    const handlePrevMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
        setSelectedDate(null);
        setSelectedDateEvents([]);
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
        setSelectedDate(null);
        setSelectedDateEvents([]);
    };

    // --- NEW: Handle Today Click ---
    const handleTodayClick = () => {
        const now = new Date();
        setCurrentDate(now); // Switches the calendar view to current month
        
        // Calculate the date string for "Today"
        const currentYear = now.getFullYear();
        const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
        const currentDay = String(now.getDate()).padStart(2, '0');
        const nowStr = `${currentYear}-${currentMonth}-${currentDay}`;
        
        setSelectedDate(nowStr);

        // If we are already on the current month, map immediately.
        // If we are switching months, the useEffect[events] will handle the mapping after fetch.
        if (events[nowStr]) {
            setSelectedDateEvents(events[nowStr]);
        } else {
             // Clear while loading if switching months
             setSelectedDateEvents([]);
        }

        // Scroll to details view
        setTimeout(() => {
            if (detailsRef.current) {
                detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 100);
    };

    // --- NEW: Logic split out for just *viewing* a date's details ---
    const handleViewDate = (dateStr) => {
        if (!dateStr) {
            setSelectedDate(null);
            setSelectedDateEvents([]);
            return;
        }

        const dayEvents = events[dateStr] || [];
        setSelectedDate(dateStr);
        setSelectedDateEvents(dayEvents); // Set the events for the list
        
        // Scroll to details only if there are events
        if (dayEvents.length > 0) {
            setTimeout(() => {
                if (detailsRef.current) {
                    detailsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                    });
                }
            }, 0);
        }
    };

    // --- NEW: Specific Handler for Toggle Selection (Circle Click) ---
    const handleDateSelectionToggle = (e, dateStr) => {
        e.stopPropagation(); // Prevents the parent cell click (viewing details)
        const newDates = new Set(specificDownloadDates);
        if (newDates.has(dateStr)) {
            newDates.delete(dateStr);
        } else {
            newDates.add(dateStr);
        }
        setSpecificDownloadDates(newDates);
    };

    // --- MODIFIED: Master click handler for a day cell ---
    const handleDayCellClick = (dateStr) => {
        if (!dateStr) return; // Should not happen if isCurrentMonth is true, but good safety

        // Only View details (Scroll to bottom), logic for selection is moved to the circle
        handleViewDate(dateStr);
    };
    
    // --- MODIFIED: Download function with STRING based parsing to prevent timezone shift ---
    const handleDownloadExcel = async () => {
        
        setIsDownloading(true);
        let eventsToDownload = [];
        let params = {};

        try {
            // --- Logic to determine which dates to fetch ---
            if (downloadMode === 'range') {
                if (!downloadStartDate || !downloadEndDate) {
                    alert('Please select a Start Date and End Date for the download.');
                    setIsDownloading(false);
                    return;
                }
                if (new Date(downloadEndDate) < new Date(downloadStartDate)) {
                    alert('End Date cannot be before Start Date.');
                    setIsDownloading(false);
                    return;
                }
                
                // --- STEP 1: Fetch extra day to ensure we get late events of the 9th ---
                // Treat input string as UTC midnight to avoid timezone shifting
                const endDateObj = new Date(downloadEndDate + 'T00:00:00Z');
                endDateObj.setUTCDate(endDateObj.getUTCDate() + 1);
                const adjustedEndDate = endDateObj.toISOString().split('T')[0];

                params = {
                    startDate: downloadStartDate,
                    endDate: adjustedEndDate, // Use the adjusted date (the 10th)
                };

                const response = await api.get('/records/hearing-events', { params });
                
                // --- STEP 2: Strict Filter to remove the 10th ---
                // Since we fetched 'upto' the 10th, we now filter to keep only dates <= selected End Date (the 9th)
                eventsToDownload = response.data.filter(event => {
                    const eventDateStr = String(event.hearingDate).split('T')[0];
                    return eventDateStr >= downloadStartDate && eventDateStr <= downloadEndDate;
                });

            } else if (downloadMode === 'specific') {
                const dateStrings = Array.from(specificDownloadDates);

                if (dateStrings.length === 0) {
                    alert('Please click on the calendar to select at least one date to download.');
                    setIsDownloading(false);
                    return;
                }

                const dates = dateStrings.map(ds => new Date(ds + 'T00:00:00Z'));
                const minDate = new Date(Math.min.apply(null, dates));
                const maxDate = new Date(Math.max.apply(null, dates));
                maxDate.setUTCDate(maxDate.getUTCDate() + 1);

                params = {
                    startDate: minDate.toISOString().split('T')[0],
                    endDate: maxDate.toISOString().split('T')[0],
                };
                
                const response = await api.get('/records/hearing-events', { params });
                
                // --- FIXED FILTER LOGIC: Use String split instead of new Date() ---
                const specificDateSet = new Set(dateStrings);
                const allFetchedEvents = response.data;
                
                eventsToDownload = allFetchedEvents.filter(event => {
                    // Force string parsing
                    const dateStr = String(event.hearingDate).split('T')[0];
                    return specificDateSet.has(dateStr);
                });
            }
            // --- End of fetch logic ---


            if (eventsToDownload.length === 0) {
                alert('No events found for the selected dates.');
                setIsDownloading(false);
                return;
            }

            // 2. Group events by date using STRING manipulation (Safe from Timezones)
            const groupedEvents = new Map();
            eventsToDownload.forEach(event => {
                // FIXED: Do not use new Date(). Use simple string splitting.
                const dateStr = String(event.hearingDate).split('T')[0];
                
                if (!groupedEvents.has(dateStr)) {
                    groupedEvents.set(dateStr, []);
                }
                groupedEvents.get(dateStr).push(event);
            });

            // 3. Sort the map by date (keys)
            const sortedGroupedEvents = new Map([...groupedEvents.entries()].sort());

            // 4. Build the Excel Array (AOA)
            const excelHeaders = [
                'PROVIDR',
                'PATIENT NAME',
                'ADJ Number',
                'CASE STATUS',
                'HEARING DATE',
                'HEARING TIME',
                'VENUE or BOARD LOCATION',
                'HEARING TYPE',
                'PMR STATUS',
                'DOR FILED BY',
                'STATUS 4903.8',
                'JUDGE ORDER STATUS',
                'JUDGE NAME',
                'VIRTUAL COURTROOM LINK',
                'CONTACT NUMBER', 
                'ACCESS CODE',
            ];
      
            const numCols = excelHeaders.length;
            
            const dataForSheet = [excelHeaders];
            const merges = [];
            let rowIndex = 1; // Start at 1 because headers are in row 0
            const dateRowIndexes = new Set(); // Keep track of date rows for styling

            // Loop through each date in our sorted Map
            for (const [dateStr, eventsOnDay] of sortedGroupedEvents.entries()) {
                
                if (rowIndex > 1) {
                    dataForSheet.push([]); // Add blank row
                    rowIndex++;
                }

                // 4b. Add the Date Header Row
                const date = new Date(dateStr + 'T00:00:00'); // Adding time ensures local parsing for header text
                const dateOptions = { weekday: 'long' };
                const dateStrOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
                const formattedDateHeader = `${date.toLocaleDateString('en-US', dateOptions)} ${date.toLocaleDateString('en-US', dateStrOptions)}`;
                
                dataForSheet.push([formattedDateHeader]);
                
                merges.push({ 
                    s: { r: rowIndex, c: 0 }, // Start cell (row, col)
                    e: { r: rowIndex, c: numCols - 1 } // End cell (row, col)
                });
                dateRowIndexes.add(rowIndex); // Add this row index for styling
                rowIndex++;

                // 4d. Sort events for this day by time and add them
                eventsOnDay.sort((a, b) => (a.hearingTime || '00:00').localeCompare(b.hearingTime || '00:00'));
                
                for (const event of eventsOnDay) {
                    // --- MODIFIED: Date Formatting for DD-MM-YYYY using STRING split ---
                    // This is the specific fix for the Excel column.
                    // We rely on the dateStr key (which came from string split) to form the date.
                    const [yearStr, monthStr, dayStr] = dateStr.split('-');
                    const formattedHearingDate = `${dayStr}-${monthStr}-${yearStr}`;
                    // --- End of Date Formatting ---

                    // --- NEW: Time Formatting Logic (Only AM/PM) ---
                    let hearingTimeDisplay = event.hearingTime || '';
                    if (hearingTimeDisplay && hearingTimeDisplay.includes(':')) {
                        const [hours] = hearingTimeDisplay.split(':').map(Number);
                        hearingTimeDisplay = hours >= 12 ? 'PM' : 'AM';
                    }

                    const eventRow = [
                        event.provider || '',
                        event.ptName || '',
                        event.adjNumber && event.adjNumber[0].value ? event.adjNumber[0].value : (event.adjNumber || '').toString(), // First ADJ Number
                        event.caseStatus || '',
                        formattedHearingDate,
                        event.hearingTime || '', 
                        event.boardLocation || '',
                        event.hearingStatus ||'', 
                        event.pmrStatus || '',
                        event.dorFiledBy || '',
                        event.status4903_8 || '',
                        event.judgeOrderStatus || '',
                        event.judgeName || '',
                        event.courtRoomlink || '',
                        event.judgePhone || '', // This is 'CONTACT'
                        event.AccesCode || '',
                        
                    ];
                    
                    dataForSheet.push(eventRow);
                    rowIndex++;
                }
            }

            // 5. Create worksheet and workbook
            const ws = XLSX.utils.aoa_to_sheet(dataForSheet);
            ws['!merges'] = merges;

            // --- Define Styles ---
            const headerStyle = {
                fill: { fgColor: { rgb: "008080" } }, // Teal/Cyan color from screenshot
                font: { bold: true, color: { rgb: "000000" } }, // Black text
                alignment: { horizontal: "center", vertical: "center" }
            };
            
            const dateRowStyle = {
                fill: { fgColor: { rgb: "364150" } }, // Dark Blue-Grey from screenshot
                font: { bold: true, color: { rgb: "FFFFFF" } }, // White text
                alignment: { horizontal: "left", vertical: "center", indent: 1 } // Left-aligned with indent
            };

            // --- Apply Styles ---
            
            // 1. Style the main header row (Row 1, index 0)
            const headerRange = XLSX.utils.decode_range(`A1:${XLSX.utils.encode_col(numCols - 1)}1`);
            for (let c = headerRange.s.c; c <= headerRange.e.c; c++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: c });
                if (ws[cellAddress]) {
                    ws[cellAddress].s = headerStyle;
                }
            }

            // 2. Style the date rows
            dateRowIndexes.forEach(r => {
                const dateCellAddress = XLSX.utils.encode_cell({ r: r, c: 0 });
                if (ws[dateCellAddress]) {
                    ws[dateCellAddress].s = dateRowStyle;
                }
            });
            
            // Set column widths
            const colWidths = [
                { wch: 15 }, // ADJ
                { wch: 15 }, // HEARING DATE
                { wch: 25 }, // APPLICANT
                { wch: 15 }, // PROVDR
                { wch: 10 }, // TIME
                { wch: 10 }, // VENUE
                { wch: 15 }, // HEARING TYP
                { wch: 25 }, // JUDGE
                { wch: 30 }, // VIRTUAL ROOM LINK
                { wch: 15 }, // CONTACT
                { wch: 15 }, // ACCESS CODE
                { wch: 15 }, // CASE STATUS
            ];
            ws['!cols'] = colWidths;

            // --- Set Row Heights ---
            const sheetRows = [];
            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let r = range.s.r; r <= range.e.r; r++) {
                if (r === 0) { // Header row
                    sheetRows[r] = { hpx: 30 }; // Taller header row
                } else if (dateRowIndexes.has(r)) { // Date row
                    sheetRows[r] = { hpx: 25 }; // Taller date row
                }
            }
            ws['!rows'] = sheetRows;


            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Hearing Events');
            
            // 6. Trigger download
            XLSX.writeFile(wb, 'Hearing_Events_Styled.xlsx');

        } catch (err) {
            console.error('Failed to download Excel:', err);
            alert('Failed to download Excel file. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };


    // UI Helpers
    const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="w-full bg-white rounded-lg p-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <CalendarDays className="w-6 h-6" />
                        Hearing Calendar
                    </h2>
                    
                    {/* --- MODIFIED: Download Section with Mode Toggle --- */}
                    <div className="flex flex-wrap items-start gap-2 border-l pl-3 ml-3">
                        
                        {/* --- Download Mode Toggle --- */}
                        <div className="flex flex-col gap-1 text-xs pt-1">
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                    type="radio"
                                    name="downloadMode"
                                    value="range"
                                    checked={downloadMode === 'range'}
                                    onChange={() => setDownloadMode('range')}
                                    className="h-3 w-3"
                                />
                                Date Range
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                    type="radio"
                                    name="downloadMode"
                                    value="specific"
                                    checked={downloadMode === 'specific'}
                                    onChange={() => setDownloadMode('specific')}
                                    className="h-3 w-3"
                                />
                                Specific Dates
                            </label>
                        </div>

                        {/* --- Conditional UI --- */}
                        {downloadMode === 'range' ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={downloadStartDate}
                                    onChange={(e) => setDownloadStartDate(e.target.value)}
                                    className="text-xs p-1.5 border border-gray-300 rounded-md"
                                    max={downloadEndDate || undefined}
                                    title="Download Start Date"
                                />
                                <span className="text-xs text-gray-500">to</span>
                                <input
                                    type="date"
                                    value={downloadEndDate}
                                    onChange={(e) => setDownloadEndDate(e.target.value)}
                                    className="text-xs p-1.5 border border-gray-300 rounded-md"
                                    min={downloadStartDate || undefined}
                                    title="Download End Date"
                                />
                            </div>
                        ) : (
                            // --- NEW: UI for Specific Date selection ---
                            <div className="flex flex-col gap-1 items-start w-64">
                                <p className="text-xs text-gray-600">
                                    Click circles on dates to select for download.
                                </p>
                                {specificDownloadDates.size > 0 && (
                                    <button
                                        onClick={() => setSpecificDownloadDates(new Set())}
                                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                    >
                                        <XCircle className="w-3 h-3" />
                                        Clear {specificDownloadDates.size} selected date(s)
                                    </button>
                                )}
                            </div>
                        )}

                        <button
                            onClick={handleDownloadExcel}
                            disabled={
                                (downloadMode === 'range' && (!downloadStartDate || !downloadEndDate)) ||
                                (downloadMode === 'specific' && specificDownloadDates.size === 0) || // Updated logic
                                isDownloading
                            }
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors self-start"
                            title="Download Excel"
                        >
                            <Download className="w-4 h-4" />
                            {isDownloading ? 'Downloading...' : 'Download'}
                        </button>
                    </div>
                </div>
                
                <div className="flex items-center gap-4">
                      {/* --- NEW: Today Button --- */}
                      <button 
                        onClick={handleTodayClick}
                        className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-md text-sm font-medium hover:bg-indigo-100 transition-colors flex items-center gap-1"
                    >
                       <Calendar className="w-4 h-4" /> Today
                    </button>

                    <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <h3 className="text-lg font-semibold text-gray-700 min-w-[150px] text-center">
                        {monthNames[month]} {year}
                    </h3>
                    <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
                </div>
            )}

            {/* Error */}
            {error && !loading && (
                 <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p>{error}</p>
                </div>
            )}

            {/* Calendar */}
            {!loading && !error && (
                <>
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1 mb-4">
                        {dayNamesShort.map(day => <div key={day} className="text-center font-semibold text-gray-600 py-2">{day}</div>)}
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1 mb-6">
                        {calendarGrid.map((dayInfo) => {
                            const isToday = dayInfo.dateStr === todayStr;
                            const hasEvents = dayInfo.events.length > 0;
                            // --- MODIFIED: Separate selection states ---
                            const isSelectedForView = selectedDate === dayInfo.dateStr; // For viewing details below
                            const isSelectedForDownload = downloadMode === 'specific' && specificDownloadDates.has(dayInfo.dateStr); // For download list

                            return (
                                <div
                                    key={dayInfo.key}
                                    onClick={() => dayInfo.isCurrentMonth && handleDayCellClick(dayInfo.dateStr)}
                                    className={`relative min-h-[90px] p-2 border rounded-lg transition-all flex flex-col ${
                                        !dayInfo.isCurrentMonth ? 'bg-gray-50 cursor-default' : 'hover:bg-gray-50 cursor-pointer'
                                    } ${
                                        // --- NEW: Styling logic for download selection ---
                                        isSelectedForDownload ? 'bg-indigo-50 border-indigo-400' :
                                        isToday ? 'bg-blue-50 border-blue-400' :
                                        hasEvents && !isToday && dayInfo.isCurrentMonth ? 'bg-yellow-50' :
                                        'border-gray-200'
                                    } ${
                                        isSelectedForView ? 'ring-2 ring-indigo-500' : '' // Ring for "viewing"
                                    }`}
                                >
                                    {/* --- NEW: Selection Circle UI --- */}
                                    {downloadMode === 'specific' && dayInfo.isCurrentMonth && (
                                         <div 
                                            onClick={(e) => handleDateSelectionToggle(e, dayInfo.dateStr)} // Only toggles selection
                                            className={`absolute top-1 right-1 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all z-10 cursor-pointer ${
                                              isSelectedForDownload 
                                                ? 'bg-indigo-600 border-indigo-600' 
                                                : 'border-gray-300 bg-white hover:border-indigo-400'
                                          }`}>
                                            {isSelectedForDownload && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                         </div>
                                    )}

                                    {dayInfo.isCurrentMonth && (
                                        <>
                                            <div className="font-medium text-sm mb-1 self-start">{dayInfo.day}</div>
                                            {hasEvents && (
                                                <div className="mt-auto space-y-1 text-center">
                                                    <div className="text-xs font-semibold text-gray-700 bg-gray-200 rounded-full px-2 py-0.5">
                                                        {dayInfo.events.length} event{dayInfo.events.length > 1 ? 's' : ''}
                                                    </div>
                                                    {dayInfo.hasPastDue && (
                                                        <div className="text-xs text-red-600 font-bold flex items-center justify-center gap-1">
                                                            <AlertTriangle className="w-3 h-3" /> Past due
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* This is where the Day List is rendered */}
                    <div ref={detailsRef}>
                        {selectedDate && (
                            <HearingDayList
                                selectedDate={selectedDate}
                                events={selectedDateEvents}
                                onViewDetails={onViewDetails}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default HearingCalendar;